import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  ArrowRightIcon,
  Chip,
  EyeIcon,
  PencilSimpleIcon,
  ProgressBar,
  Tooltip,
  UserIcon,
  VisuallyHidden,
  cn,
  focusableTriggerClasses,
} from "@stacklet/ui";
import pluralize from "pluralize";
import { graphql, useLazyLoadQuery } from "react-relay";
import { generatePath } from "react-router";

import { Link } from "app/components/links";
import { RbacChip } from "app/components/rbac/RbacChip";
import { RoleAssignmentTable } from "app/components/rbac/RoleAssignmentTable";
import { UserBrowser } from "app/components/rbac/UserBrowser";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  PaginationFooter,
  StackletGrid,
  type StackletGridPagination,
} from "app/components/table/StackletGrid";
import { SEARCH_DEBOUNCE_MS } from "app/constants";
import { paths } from "app/constants/paths";
import { useHasScopePermission } from "app/hooks";
import {
  firstPage,
  useDebouncedCallback,
  useSectionPagination,
  useStickyPageSize,
  type SectionPageInfo,
  type SectionPage,
} from "app/hooks/ui";

import {
  buildGroupFilter,
  providerLabel,
  type GroupSection,
  type Group,
  type GroupsView,
} from "./groupSections";
import {
  GroupSectionCell,
  DisclosureToggle,
  GroupNameCell,
} from "./GroupSectionsGridCells";

import type { GroupSectionsGridGroupsQuery } from "./__generated__/GroupSectionsGridGroupsQuery.graphql";
import type { GridColDef } from "@mui/x-data-grid-pro";

export type { GroupSection, GroupsView } from "./groupSections";

/** Visibility-scope chips shown per row before the rest collapse to "+N". */
const MAX_VISIBILITY_CHIPS = 2;
/** Number of leaf columns; section, panel and pager rows span them all. */
const COLUMN_COUNT = 5;
/**
 * Columns held at their declared width, exempt from content autosizing: the
 * disclosure caret, the member count and the row actions are fixed-size
 * content, so sharing leftover width into them would just pad them out.
 */
const FIXED_WIDTH_FIELDS = ["disclosure", "memberCount", "actions"];
/**
 * Kept in sync with the `roleAssignments(first:)` cap in the query below; used
 * to infer truncation when the connection reports no `pageInfo.total`.
 */
const ROLE_ASSIGNMENTS_PAGE_SIZE = 100;

// Compact cell padding. The grid has no header row of its own (see
// `columnHeaderHeight` below): per the design, headers appear only inside an
// expanded group, above its role assignments or members.
const GRID_CLASS_NAMES = [
  "[&_.MuiDataGrid-cell]:!px-xs",
  // Zero the panel cell's own padding so its background fills edge to edge (its
  // inner px-lg still insets content). Direct child (`>`) only, else it'd strip
  // padding from the nested UserBrowser grid's cells too.
  "[&_.panel-row>.MuiDataGrid-cell]:!p-0",
].join(" ");

/** What a loaded section holds: its current group page and that page's cursors. */
interface SectionGroupsData {
  groups: Group[];
  pageInfo: SectionPageInfo;
}

/** A section header row. */
interface SectionRow {
  kind: "section";
  id: string;
  section: GroupSection;
  /** Whether the section's groups are loading (drives the header's loading bar). */
  isLoading: boolean;
}

/** A group row beneath a section. */
interface GroupRow {
  kind: "group";
  id: string;
  section: GroupSection;
  group: Group;
  /** Whether this group's detail panel is open (drives the caret). */
  isExpanded: boolean;
}

/** A group's expanded detail panel row. */
interface PanelRow {
  kind: "panel";
  id: string;
  group: Group;
}

/** A section's pagination footer row. */
interface PagerRow {
  kind: "pager";
  id: string;
  section: GroupSection;
  pagination: StackletGridPagination;
}

/** A grid row: a section header, a group, an expanded panel, or a section's pager. */
type Row = SectionRow | GroupRow | PanelRow | PagerRow;

/** The loaded group node a {@link Group} is denormalised from. */
type LoadedGroupNode = Group["principalRef"];

/**
 * The group's provider, from its `source`. The label is resolved the same way
 * the section headers resolve theirs; a provider type Relay surfaces as `%other`
 * has neither a display name nor a name, so the namespace stands in.
 */
function providerFromSource(
  source: LoadedGroupNode["source"],
): Group["provider"] {
  if (!source) return null;
  return {
    idpType: source.idpType,
    label: providerLabel({
      displayName: "displayName" in source ? source.displayName : null,
      idpAlias: source.idpAlias,
      name: ("name" in source ? source.name : null) ?? source.idpNamespace,
    }),
  };
}

/** Denormalises a loaded group node into a group row. Colocated with the query
 * whose fields it reads. */
function groupFromNode(node: LoadedGroupNode): Group {
  const assignments = (node.roleAssignments.edges ?? []).map(
    (edge) => edge.node,
  );

  // pageInfo.total is best-effort (nullable); fall back to inferring truncation
  // from a full page of fetched edges.
  const total = node.roleAssignments.pageInfo.total;
  const hasMoreAssignments =
    total != null
      ? total > assignments.length
      : assignments.length >= ROLE_ASSIGNMENTS_PAGE_SIZE;

  return {
    id: node.id,
    uuid: node.uuid,
    name: node.name,
    label: node.displayName || node.name,
    provider: providerFromSource(node.source),
    memberCount: node.members.pageInfo.total ?? null,
    hasAdmin: assignments.some(({ role }) => role.name === "admin"),
    visibilityTargets: assignments
      .filter(({ role }) => role.name === "resource-viewer")
      .map(({ target }) =>
        target.__typename === "AccountGroup" ? target.name : "",
      )
      .filter(Boolean),
    hasMoreAssignments,
    principalRef: node,
  };
}

/**
 * The key a section's page, loaded groups and loading state are stored under.
 * Cursors are only valid for the scope they were fetched under, so the scope
 * is part of the key: a section whose scope changes under the same id (the
 * flat section, as the provider filter narrows it) starts over on its first
 * page, rather than refetching a different list with a cursor from the old one.
 */
function sectionKey(section: GroupSection): string {
  return `${section.id}:${JSON.stringify(section.scope)}`;
}

interface GroupsLoaderProps {
  /** The section whose groups to load. */
  section: GroupSection;
  /** The current group page's cursor window. */
  page: SectionPage;
  /** Active search term, matched against group name. */
  search: string;
  /** Called with the loaded groups, under the section's pagination key. */
  onLoaded: (sectionId: string, data: SectionGroupsData) => void;
}

/** Loads one section's current group page and reports it up; renders nothing. */
function GroupsLoader({ section, page, search, onLoaded }: GroupsLoaderProps) {
  const data = useLazyLoadQuery<GroupSectionsGridGroupsQuery>(
    graphql`
      query GroupSectionsGridGroupsQuery(
        $filterElement: FilterElementInput
        $first: Int
        $last: Int
        $after: String
        $before: String
      ) {
        userGroups(
          filterElement: $filterElement
          first: $first
          last: $last
          after: $after
          before: $before
          order: "name"
        ) {
          edges {
            node {
              displayName
              id
              members(first: 1) {
                pageInfo {
                  total
                }
              }
              name
              roleAssignments(first: 100) {
                edges {
                  node {
                    id
                    role {
                      name
                    }
                    target {
                      __typename
                      ... on AccountGroup {
                        name
                      }
                    }
                  }
                }
                pageInfo {
                  total
                }
              }
              ...RoleAssignmentTable_principal
              source {
                __typename
                idpAlias
                idpNamespace
                idpType
                ... on CloudOrganization {
                  name
                }
                ... on SAMLProvider {
                  displayName
                  name
                }
              }
              uuid
            }
          }
          pageInfo {
            endCursor
            hasNextPage
            startCursor
            total
          }
        }
      }
    `,
    {
      filterElement: buildGroupFilter(section.scope, search),
      first: page.first,
      last: page.last,
      after: page.after,
      before: page.before,
    },
  );

  const result = useMemo<SectionGroupsData>(() => {
    // The schema types the connection as non-null, but Relay can still hand
    // back a payload without it (e.g. a field-level error on the section's
    // query); read it defensively so one section can't crash the whole grid.
    const connection = data.userGroups as typeof data.userGroups | undefined;
    return {
      groups: (connection?.edges ?? [])
        .map((edge) => edge.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map(groupFromNode),
      pageInfo: {
        hasNextPage: connection?.pageInfo.hasNextPage ?? false,
        startCursor: connection?.pageInfo.startCursor ?? null,
        endCursor: connection?.pageInfo.endCursor ?? null,
        total: connection?.pageInfo.total ?? null,
      },
    };
  }, [data]);

  const key = sectionKey(section);
  useEffect(() => {
    onLoaded(key, result);
  }, [key, result, onLoaded]);
  return null;
}

interface Props {
  /** The provider sections to list, each expandable. */
  sections: GroupSection[];
  /** What an expanded group row shows: its role assignments or its members. */
  view: GroupsView;
  /** Whether the section list is still resolving upstream. */
  loading?: boolean;
  /** Singular noun for one section, used in the empty state, e.g. "provider". */
  sectionUnit?: string;
  /** Toolbar content before the Columns button — the view toggle / filter. */
  toolbarActions?: ReactNode;
  /** Storage key for the grid's sticky column state. */
  tableId: string;
}

/**
 * Every identity provider's groups and the role assignments each group holds,
 * grouped into per-provider sections within one aligned grid. Expanding a group
 * shows either its full role assignments (the shared {@link RoleAssignmentTable})
 * or its members (the shared {@link UserBrowser}), per the {@link GroupsView}
 * toggle. Each section loads its groups on its own, scoped by the provider's
 * `idp-namespace`, so the filter that chooses which sections appear is applied
 * server-side.
 */
export function GroupSectionsGrid({
  sections,
  view,
  loading,
  sectionUnit = "provider",
  toolbarActions,
  tableId,
}: Props) {
  const hasPermission = useHasScopePermission();
  const canManageGroups = hasPermission("system", "EDIT");

  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const toggleGroup = useCallback((id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Groups per page, shared by every section and remembered per grid, so the
  // size picked in one section's footer applies to all and survives a reload.
  const [pageSize, setStoredPageSize] = useStickyPageSize(
    tableId,
    DEFAULT_PAGE_SIZE_OPTIONS,
  );

  // Per-section cursor pagination for the groups under each section. Sections
  // are always open, so `expanded`/`toggle` go unused (the group-panel toggle
  // is separate).
  const { pageById, dataById, loadingById, setPageSize, onLoaded, getPager } =
    useSectionPagination<SectionGroupsData>({
      pageSize,
      onPageSizeChange: setStoredPageSize,
    });

  // `search` is the live field value; `appliedSearch` is the term the loaded
  // groups were fetched with. Debounced so a keystroke doesn't refetch every
  // section on each character.
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const applySearch = useDebouncedCallback((value: string) => {
    // Skip an unchanged term: refetching it returns the same data, and the
    // loading flags set below only clear when new data arrives.
    if (value.trim() === appliedSearch.trim()) return;

    setAppliedSearch(value);
    // A new filter invalidates each section's cursors, so send every section back to
    // its first page. Flagging them loading also shows each section's bar (and
    // disables its pager) until the refetched page lands.
    for (const section of sections) {
      setPageSize(sectionKey(section), pageSize);
    }
  }, SEARCH_DEBOUNCE_MS);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    applySearch.run(value);
  };

  const isSearching = appliedSearch.trim() !== "";

  const rows: Row[] = useMemo(
    () =>
      sections.flatMap((section) => {
        const key = sectionKey(section);
        const loaded = dataById[key];
        // A headerless section (the flat, ungrouped list) has no row of its
        // own; its loading state goes on the grid instead (see `loading` below).
        const sectionRows: Row[] = section.hideHeader
          ? []
          : [
              {
                kind: "section",
                id: `section:${section.id}`,
                section,
                isLoading: !loaded || Boolean(loadingById[key]),
              },
            ];
        if (!loaded) return sectionRows;

        // A provider with no group matching the search term is hidden outright
        // rather than shown as an empty section.
        if (isSearching && loaded.groups.length === 0) return [];

        const groupRows: Row[] = loaded.groups.flatMap((group) => {
          const isExpanded = expandedGroups.has(group.id);
          const groupRow: GroupRow = {
            kind: "group",
            id: `${section.id}:${group.id}`,
            section,
            group,
            isExpanded,
          };
          return isExpanded
            ? [
                groupRow,
                {
                  kind: "panel" as const,
                  id: `panel:${section.id}:${group.id}`,
                  group,
                },
              ]
            : [groupRow];
        });

        const pager = getPager(key, {
          pageInfo: loaded.pageInfo,
          rowCount: loaded.groups.length,
          rowUnit: "group",
        });
        if (!pager) return [...sectionRows, ...groupRows];

        return [
          ...sectionRows,
          ...groupRows,
          {
            kind: "pager" as const,
            id: `pager:${section.id}`,
            section,
            pagination: pager,
          },
        ];
      }),
    [sections, dataById, loadingById, expandedGroups, getPager, isSearching],
  );

  // A headerless section has no header row to carry its loading bar, so while
  // its groups load the grid as a whole reports loading.
  const isFlatLoading = sections.some((section) => {
    const key = sectionKey(section);
    return section.hideHeader && (!dataById[key] || Boolean(loadingById[key]));
  });

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        // The leading disclosure column: a group row's expand caret. Section,
        // panel and pager rows span the whole grid from here, so the section
        // header's icon sits flush left, under the caret.
        field: "disclosure",
        headerName: "",
        width: 44,
        minWidth: 44,
        maxWidth: 44,
        sortable: false,
        // Structural, not data: hiding it would remove every expand control,
        // and with it the only way to reach an expanded group's role assignments or members. So the
        // Columns picker doesn't offer it.
        hideable: false,
        align: "center",
        // Reads as part of the name column, so no divider after it.
        cellClassName: "!border-r-0",
        colSpan: (_, row) => (row.kind === "group" ? 1 : COLUMN_COUNT),
        renderCell: ({ row }) => {
          if (row.kind === "section") {
            return (
              <GroupSectionCell
                isLoading={row.isLoading}
                section={row.section}
              />
            );
          }
          if (row.kind === "pager") {
            return (
              <div className="flex h-full w-full items-center">
                <PaginationFooter {...row.pagination} />
              </div>
            );
          }
          if (row.kind === "panel") {
            return <PanelCell group={row.group} view={view} />;
          }
          return (
            <DisclosureToggle
              isExpanded={row.isExpanded}
              label={row.group.label}
              onToggle={() => toggleGroup(row.group.id)}
            />
          );
        },
      },
      {
        field: "label",
        headerName: "Name",
        flex: 1,
        minWidth: 260,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === "group" ? <GroupNameCell group={row.group} /> : null,
      },
      {
        // At-a-glance access: resource-viewer scope chips from the left, and
        // an Admin badge pinned to the right edge. The expanded panel has the
        // full role-assignment picture.
        field: "access",
        headerName: "Role Assignments",
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.kind !== "group") return null;
          const shown = row.group.visibilityTargets.slice(
            0,
            MAX_VISIBILITY_CHIPS,
          );
          const hidden =
            row.group.visibilityTargets.slice(MAX_VISIBILITY_CHIPS);

          return (
            <div className="flex h-full w-full items-center gap-xs">
              {shown.length > 0 ? (
                <EyeIcon
                  aria-label="Visibility scope"
                  className="size-4 shrink-0 text-text-secondary"
                />
              ) : null}
              {shown.map((target) => (
                <RbacChip
                  key={target}
                  label={target}
                  rbacType="column-account-group"
                  variant="scope"
                />
              ))}
              {hidden.length > 0 ? (
                // Hover-only tooltip naming the collapsed account groups, so
                // the label is focusable (the tooltip opens on keyboard focus)
                // and names them for screen readers as visually hidden text.
                <Tooltip
                  title={
                    <ul>
                      {hidden.map((target) => (
                        <li key={target}>{target}</li>
                      ))}
                    </ul>
                  }
                  asChild
                >
                  <span
                    className={cn(
                      "text-label-small text-text-secondary",
                      focusableTriggerClasses,
                    )}
                    tabIndex={0}
                  >
                    <span>+{hidden.length}</span>
                    <VisuallyHidden>: {hidden.join(", ")}</VisuallyHidden>
                  </span>
                </Tooltip>
              ) : null}
              {/*
                The summary is computed from the first page of assignments, so
                say it is incomplete rather than presenting it as the whole
                picture (e.g. an admin grant beyond the page would be missing).
              */}
              {row.group.hasMoreAssignments ? (
                <span className="text-label-small text-text-secondary">
                  + more
                </span>
              ) : null}
              {row.group.hasAdmin ? (
                <Chip className="ml-auto" color="dark-grey">
                  Admin
                </Chip>
              ) : null}
            </div>
          );
        },
      },
      {
        // Hugs its content: an icon and a short count.
        field: "memberCount",
        headerName: "# Members",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === "group" ? (
            <div className="flex h-full items-center gap-xs">
              <UserIcon
                aria-label={`${row.group.label} members`}
                className="size-4 shrink-0 text-text-secondary"
              />
              <span>{row.group.memberCount ?? "--"}</span>
            </div>
          ) : null,
      },
      {
        field: "actions",
        headerName: "",
        width: 88,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.kind !== "group") return null;
          return (
            // Flex (not MUI `align`) with the section header's gap so the two
            // rows' edit/arrow align; the buttons' p-md gives the hit area.
            <div className="flex h-full w-full items-center justify-end gap-xxs">
              {canManageGroups ? (
                <Link
                  className="inline-flex p-md text-text-secondary hover:text-text-primary"
                  textDecoration="none"
                  to={generatePath(paths.groupEdit, {
                    groupUuid: row.group.uuid,
                  })}
                >
                  <PencilSimpleIcon
                    aria-label={`Edit ${row.group.label}`}
                    className="size-4"
                  />
                </Link>
              ) : null}
              <Link
                className="inline-flex p-md text-text-secondary hover:text-text-primary"
                textDecoration="none"
                to={generatePath(paths.groupDetails, {
                  groupUuid: row.group.uuid,
                })}
              >
                <ArrowRightIcon
                  aria-label={`View ${row.group.label}`}
                  size={16}
                />
              </Link>
            </div>
          );
        },
      },
    ],
    // Keep `columns` stable across expand — cells read their state off `row`,
    // not closures — or the grid re-autosizes and the columns visibly re-fit.
    [view, canManageGroups, toggleGroup],
  );

  return (
    <>
      {/* One loader per section fills its current group page; paging, a search
          term or a change of scope refetches it. */}
      {sections.map((section) => (
        <Suspense fallback={null} key={sectionKey(section)}>
          <GroupsLoader
            onLoaded={onLoaded}
            page={pageById[sectionKey(section)] ?? firstPage(pageSize)}
            search={appliedSearch}
            section={section}
          />
        </Suspense>
      ))}

      <StackletGrid
        className={GRID_CLASS_NAMES}
        // No header row: the design puts column headers under an expanded
        // group (its role assignment / member table), not over the sections. MUI
        // sizes the headers container to this and clips its contents, so zero
        // removes the row while the column definitions (and the Columns
        // picker they feed) stay as they are.
        columnHeaderHeight={0}
        columns={columns}
        defaultDensity="comfortable"
        fixedWidthFields={FIXED_WIDTH_FIELDS}
        getRowClassName={(params) => {
          if (params.row.kind === "section") return "bg-background-secondary";
          if (params.row.kind === "panel") return "panel-row";
          return "";
        }}
        getRowHeight={(params) =>
          (params.model as Row).kind === "panel" ? "auto" : 52
        }
        getRowId={(row) => row.id}
        // Force the column order so a stale persisted order (from before the
        // disclosure column existed) can't push the caret to the end.
        initialState={{
          columns: {
            orderedFields: [
              "disclosure",
              "label",
              "access",
              "memberCount",
              "actions",
            ],
          },
        }}
        loading={loading || isFlatLoading}
        onSearchChange={handleSearchChange}
        rows={rows}
        search={search}
        searchAriaLabel="Search groups"
        searchPlaceholder="Search groups"
        slotProps={{ toolbar: { className: "py-md" } }}
        slots={{
          noRowsOverlay: () => (
            <div className="flex h-full items-center justify-center text-label-small text-text-secondary">
              {isSearching
                ? "No groups match your search"
                : `No ${pluralize(sectionUnit, 0)}`}
            </div>
          ),
        }}
        tableId={tableId}
        toolbarActions={toolbarActions}
        exportTrailing
        hideDensityButton
        transparentSearch
      />
    </>
  );
}

interface PanelCellProps {
  /** The group whose detail to show. */
  group: Group;
  /** Whether to show the group's role assignments or its members. */
  view: GroupsView;
}

/** A group's expanded detail: its role assignments, or its members. Colocated
 * with the query that supplies its `...RoleAssignmentTable_principal` data. */
function PanelCell({ group, view }: PanelCellProps) {
  return (
    <div className="w-full border-b border-overlay-medium bg-background-primary px-lg py-sm">
      {view === "users" ? (
        // Search stays empty — the grid above searches groups, not members.
        <Suspense fallback={<ProgressBar size="slim" indeterminate />}>
          <UserBrowser
            enableExport={false}
            scope={{ kind: "group-uuid", groupUuid: group.uuid }}
            searchTerm=""
            // One shared id, not one per group: the id keys per-table
            // localStorage, so a per-group id would leave an entry behind for
            // every group ever expanded.
            tableId="settings-groups-group-users"
            title={null}
            fixedColumnWidths
          />
        </Suspense>
      ) : (
        <>
          <RoleAssignmentTable
            principalRef={group.principalRef}
            showEmptyRoles
          />
          {/* The table shows the first page of assignments; flag that more
           * exist rather than implying the list is complete. */}
          {group.hasMoreAssignments ? (
            <p className="pb-sm text-label-small text-text-secondary">
              Only the first {ROLE_ASSIGNMENTS_PAGE_SIZE} role assignments are
              shown.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
